import { isPatientEmbedProductionHost } from "../../utils/patient-embed-environment";

import type {
  MalnutritionCriterion,
  MalnutritionFeedback,
  MalnutritionRiskAssessment,
  MalnutritionStatus,
  PatientWeightHistory,
} from "../../types/Malnutrition";

// Mocks are controlled by the `mock` query param (no rebuild needed, unlike an
// env var). Default is off (real API); add `?mock=true` (or `mock=1`) for mocks.
export const malnutritionUseMocks = (): boolean => {
  if (typeof window === "undefined") return false; // tests / SSR: default to real API
  // Never serve fabricated patient data on a production host, even if the URL
  // asks for it — the ?mock= toggle is for local / QA / staging only.
  if (isPatientEmbedProductionHost()) return false;
  const raw = new URLSearchParams(window.location.search).get("mock");
  return raw === "true" || raw === "1";
};

const MOCK_LATENCY_MS = 120;

const resolve = <T>(value: T): Promise<T> =>
  new Promise((res) => setTimeout(() => res(value), MOCK_LATENCY_MS));

const ALL_STATUSES: MalnutritionStatus[] = [
  "high_risk",
  "medium_risk",
  "protocol",
  "following",
  "consult",
];

const readStatusFromUrl = (): MalnutritionStatus | null => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("status");
  if (!raw) return null;
  return (ALL_STATUSES as string[]).includes(raw)
    ? (raw as MalnutritionStatus)
    : null;
};

const PATIENT_BY_STATUS: Record<
  MalnutritionStatus,
  MalnutritionRiskAssessment["patient"]
> = {
  high_risk: {
    full_name: "Harden Hennepin",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    gender: "Male",
    current_unit: "4 East",
  },
  medium_risk: {
    full_name: "Andrew Doughty",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    gender: "Male",
    current_unit: "4 East",
  },
  protocol: {
    full_name: "Anna Breckenridge",
    mrn: "12345678",
    current_bed: "412A",
    age: 55,
    gender: "Female",
    current_unit: "4 East",
  },
  following: {
    full_name: "Andrew Granda",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    gender: "Male",
    current_unit: "4 East",
  },
  consult: {
    full_name: "Anna Caldera",
    mrn: "12345678",
    current_bed: "412A",
    age: 58,
    gender: "Female",
    current_unit: "4 East",
  },
};

// Shapes/values below mirror what the backend serializer actually emits (see
// api/serializers.py _malnutrition_criterion_to_fe): metric evidence ->
// Current/Prior/Change rows (raw values, no unit, "M/D" dates); signal/text
// evidence -> the "metric" kind with a single unlabeled row; quote evidence ->
// empty source_kind (not in the proto).
const FLAGGED_CRITERIA: MalnutritionCriterion[] = [
  {
    kind: "metric",
    title: "Significant weight loss",
    rows: [
      { label: "Current", value: "117.7 · 4/29" },
      { label: "Prior", value: "133.2 · 1/29" },
      { label: "Change", value: "-11.43%" },
    ],
    source: "model",
  },
  {
    // signal evidence -> single unlabeled row
    kind: "metric",
    title: "Low BMI",
    rows: [{ label: "", value: "Patient has BMI of 15.9" }],
    source: "model",
  },
  {
    kind: "quote",
    title: "Severe muscle depletion",
    quote: "Nutrition focused physical exam: severe muscle depletion.",
    source_kind: "",
    source_author: "Amber M Cree, RD",
    source_timestamp: "4/29 8:12 AM",
    source: "model",
  },
];

const PROTOCOL_CRITERIA: MalnutritionCriterion[] = [
  {
    // text evidence -> single unlabeled row
    kind: "metric",
    title: "Length of stay exceeds 7 days",
    rows: [{ label: "", value: "Current LOS: 9 days · Admitted 4/20" }],
    source: "protocol",
  },
  {
    kind: "metric",
    title: "NPO duration exceeds 5 days",
    rows: [{ label: "", value: "NPO since 4/23 · 6 days" }],
    source: "protocol",
  },
  {
    kind: "metric",
    title: "Active nutrition consult",
    rows: [{ label: "", value: "Ordered 4/22 · Malnutrition screening · Pending" }],
    source: "protocol",
  },
];

const CRITERIA_BY_STATUS: Record<MalnutritionStatus, MalnutritionCriterion[]> =
  {
    // high_risk mixes model + protocol criteria to exercise both the
    // "Why Flagged" and "Protocol" sections at once.
    high_risk: [...FLAGGED_CRITERIA, ...PROTOCOL_CRITERIA],
    medium_risk: FLAGGED_CRITERIA.slice(0, 2),
    protocol: PROTOCOL_CRITERIA,
    following: FLAGGED_CRITERIA.slice(0, 2),
    consult: FLAGGED_CRITERIA.slice(0, 3),
  };

const WEIGHT_HISTORY: PatientWeightHistory = {
  trend_percent: -7.3,
  trend_months: 0.6,
  readings: [
    { date: "4/29", weight_lb: 102, source: "Standing" },
    { date: "4/20", weight_lb: 108, source: "Bed" },
    { date: "4/12", weight_lb: 110, source: "Standing" },
    { date: "4/05", weight_lb: 113, source: "Bed" },
    { date: "3/28", weight_lb: 116, source: "Standing" },
    { date: "3/15", weight_lb: 119, source: "Outpatient" },
    { date: "2/20", weight_lb: 121, source: "Outpatient" },
    { date: "1/10", weight_lb: 123, source: "Outpatient" },
  ],
};

// In mock mode there's no backend, so remember the last recorded answer here;
// the card reads it back after the risk query is invalidated and refetched.
let mockFeedback: MalnutritionFeedback | null = null;

export const mockSetMalnutritionFeedback = (isGoodCandidate: boolean): void => {
  mockFeedback = { is_good_candidate: isGoodCandidate };
};

const buildRiskAssessment = (
  status: MalnutritionStatus,
): MalnutritionRiskAssessment => ({
  patient: PATIENT_BY_STATUS[status],
  status,
  criteria_met: CRITERIA_BY_STATUS[status],
  feedback: mockFeedback,
});

// Summary header for an unflagged patient (viewable via ?status=none): the
// header renders but there's no status banner / criteria — mirrors the API
// returning the patient summary with status=null.
const UNFLAGGED_PATIENT: MalnutritionRiskAssessment["patient"] = {
  full_name: "Jordan Vance",
  mrn: "12345678",
  current_bed: "412A",
  age: 61,
  gender: "Female",
  current_unit: "4 East",
};

export const mockGetMalnutritionRiskAssessment = (
  _emrPatientID: string,
): Promise<MalnutritionRiskAssessment> => {
  const rawStatus =
    typeof window === "undefined"
      ? null
      : new URLSearchParams(window.location.search).get("status");
  if (rawStatus === "none") {
    return resolve({
      patient: UNFLAGGED_PATIENT,
      status: null,
      criteria_met: [],
      feedback: mockFeedback,
    });
  }
  const status = readStatusFromUrl() ?? "high_risk";
  return resolve(buildRiskAssessment(status));
};

export const mockGetPatientWeights = (
  _emrPatientID: string,
): Promise<PatientWeightHistory> => resolve(WEIGHT_HISTORY);
